/* eslint-disable no-nested-ternary */
/* eslint-disable no-restricted-syntax */
import { Box, Stack, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";

import { theme } from "../../../../assets";
import { GanttChart } from "../../../../components";
import { ITaskData, TProject } from "../../../../models";
import { activities } from "../../../../services";
import { TSection, TSectionActivity } from "../tab-activities/types";

type TGanttTabParams = {
    project: TProject;
};

type SectionData = {
    id: string;
    name: string;
    resource: string | null;
    start: Date;
    end: Date;
    duration: number;
    percentComplete: number;
    dependencies: string | null;
};

type TActivitiesMap = {
    [index: number]: TSectionActivity;
};

export function recalculateTaskHours(tasks: ITaskData[]): ITaskData[] {
    const updatedTasks = [...tasks];

    updatedTasks.forEach((task) => {
        if (task.dependencies && task.dependencies.length > 0) {
            // Calculate some metrics based on dependencies if needed
            // This is a placeholder for actual business logic
        }
    });

    return updatedTasks;
}

const TabGantt = ({ project }: TGanttTabParams) => {
    const getSections = useQuery({
        queryKey: ["getSections"],
        queryFn: () =>
            activities.getSections({
                projectId: project.id_e,
                activityUserIds: undefined,
                profileIds: undefined,
                statusIds: undefined,
            }),
    });

    const { isLoading } = getSections;

    const sections: TSection[] = getSections.data?.sections ?? [];

    const convertSectionsToChartData = useCallback(() => {
        const columns: Array<{ type: string; label: string }> = [
            { type: "string", label: "Task ID" },
            { type: "string", label: "Task Name" },
            { type: "string", label: "Resource" },
            { type: "date", label: "Start Date" },
            { type: "date", label: "End Date" },
            { type: "number", label: "Duration" },
            { type: "number", label: "Percent Complete" },
            { type: "string", label: "Dependencies" },
        ];

        const findDateRange = (
            activitiesObj: TActivitiesMap
        ): { start: Date | null; end: Date | null } => {
            const activities = Object.values(activitiesObj);
            const filteredActivities = activities.filter(
                (activity) => activity.deadlineFrom && activity.deadlineTo
            );

            if (filteredActivities.length === 0) {
                return { start: null, end: null };
            }

            const startDates = filteredActivities.map((activity) =>
                activity.deadlineFrom
                    ? new Date(activity.deadlineFrom)
                    : new Date()
            );
            const endDates = filteredActivities.map((activity) =>
                activity.deadlineTo ? new Date(activity.deadlineTo) : new Date()
            );

            return {
                start: new Date(
                    Math.min(...startDates.map((date) => date.getTime()))
                ),
                end: new Date(
                    Math.max(...endDates.map((date) => date.getTime()))
                ),
            };
        };

        const processSection = (
            section: TSection,
            parentNamePath = ""
        ): SectionData[] => {
            const currentPath = parentNamePath
                ? `${section.name}/${parentNamePath}`
                : section.name;
            const { start, end } = findDateRange(section.Activities);
            const activities = Object.values(section.Activities);

            // Verifica se todas as atividades estão concluídas
            const allActivitiesCompleted = activities.every(
                (activity) => activity.finished
            );

            const totalMinutesDone = activities.reduce(
                (acc, activity) =>
                    acc + (activity.hourProgress?.minutesDone || 0),
                0
            );
            const totalExpectedMinutes = activities.reduce(
                (acc, activity) => acc + (activity.expectedMinutes || 0),
                0
            );

            let percentComplete = 0;
            if (totalExpectedMinutes > 0) {
                percentComplete = Number(
                    ((totalMinutesDone / totalExpectedMinutes) * 100).toFixed(2)
                );
            }

            if (section.Sections && section.Sections.length > 0) {
                return section.Sections.flatMap((subSection) =>
                    processSection(subSection, currentPath)
                );
            }

            if (start && end) {
                const duration = Math.round(
                    (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)
                ); // em dias

                const today = new Date();

                // Determine o resource baseando-se nas condições
                let resource = "Conforme as estimativas";
                if (today > end) {
                    resource = "Em atraso";
                }
                if (allActivitiesCompleted) {
                    resource = "Concluído";
                }
                if (percentComplete > 100 && !allActivitiesCompleted) {
                    resource = "Ultrapassou as estimativas de horas";
                }

                return [
                    {
                        id: section.id_e,
                        name: currentPath,
                        resource,
                        start,
                        end,
                        duration,
                        percentComplete,
                        dependencies: null,
                    },
                ];
            }

            return [];
        };

        const rows = sections
            .flatMap((section) => processSection(section))
            .map(
                ({
                    id,
                    name,
                    resource,
                    start,
                    end,
                    duration,
                    percentComplete,
                    dependencies,
                }) => [
                    id,
                    name,
                    resource,
                    start,
                    end,
                    duration,
                    percentComplete,
                    dependencies,
                ]
            );

        return rows.length === 0 ? false : [columns, ...rows];
    }, [sections]);

    const dataGraph = convertSectionsToChartData();

    return (
        <Box
            sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
            }}
        >
            {!isLoading ? (
                dataGraph ? (
                    <GanttChart dataGraph={dataGraph} />
                ) : (
                    <Stack
                        sx={{
                            py: "0.5rem",
                            px: "1rem",
                            gap: "1rem",
                        }}
                    >
                        <Typography
                            variant="subtitle2"
                            sx={{
                                color: theme.gray500,
                                width: "60%",
                            }}
                        >
                            Para conseguirmos gerar os dados do Gantt, é
                            necessário que as sessões possuam uma atividade e as
                            atividades dentro da sessão devem possuir uma data
                            de inicio e fim
                        </Typography>
                    </Stack>
                )
            ) : (
                <Typography>Carregando...</Typography>
            )}
        </Box>
    );
};

export default TabGantt;
